import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type Dispatch,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction,
} from 'react'

export type DockTab =
  | 'scenarios'
  | 'timeline'
  | 'inspector'
  | 'notes'
  | 'people'
  | 'settings'
  | 'account'

export type DockSetter = Dispatch<SetStateAction<DockTab>>

type DockApi = {
  dock: DockTab
  setDock: DockSetter
}

const DockCtx = createContext<DockApi | null>(null)

export function StudioDockProvider({
  children,
  dockRef,
  setDockRef,
}: {
  children: ReactNode
  dockRef: MutableRefObject<DockTab>
  setDockRef: MutableRefObject<DockSetter>
}) {
  const [dock, setDock] = useState<DockTab>('scenarios')
  dockRef.current = dock
  setDockRef.current = setDock
  const value = useMemo(() => ({ dock, setDock }), [dock])
  return <DockCtx.Provider value={value}>{children}</DockCtx.Provider>
}

export function useStudioDock(): DockApi {
  const ctx = useContext(DockCtx)
  if (!ctx) throw new Error('useStudioDock requires StudioDockProvider')
  return ctx
}

/** Rail panes. Re-renders on tab change without re-running ScenariosPage. */
export function DockPane({
  children,
}: {
  children: (dock: DockTab, setDock: DockSetter) => ReactNode
}) {
  const { dock, setDock } = useStudioDock()
  return children(dock, setDock)
}

export function DockAccountButton({
  initials,
  tone,
  onBeforeToggle,
}: {
  initials: string
  tone: { bg: string; fg: string; ring: string }
  onBeforeToggle: () => boolean
}) {
  const { dock, setDock } = useStudioDock()
  return (
    <button
      type="button"
      className={`sc-top-account${dock === 'account' ? ' on' : ''}`}
      title="Profile"
      aria-label="Profile"
      aria-pressed={dock === 'account'}
      onClick={() => {
        if (!onBeforeToggle()) return
        setDock((cur) => (cur === 'account' ? 'scenarios' : 'account'))
      }}
    >
      <span
        className="sc-top-account-av"
        style={
          {
            '--sc-presence-bg': tone.bg,
            '--sc-presence-fg': tone.fg,
            '--sc-presence-ring': tone.ring,
          } as CSSProperties
        }
        aria-hidden="true"
      >
        {initials}
      </span>
    </button>
  )
}

export function DockViewingCountButton({
  viewingCount,
  peopleViewReturnDockRef,
}: {
  viewingCount: number
  peopleViewReturnDockRef: MutableRefObject<DockTab | null>
}) {
  const { dock, setDock } = useStudioDock()
  const onPeople = dock === 'people' && Boolean(peopleViewReturnDockRef.current)
  return (
    <button
      type="button"
      className="sc-people-counts sc-top-viewing"
      title={onPeople ? 'Back to previous pane' : 'Open People'}
      aria-label={
        onPeople
          ? `${viewingCount} viewing — back to previous pane`
          : `${viewingCount} viewing — open People`
      }
      onClick={() => {
        if (dock === 'people') {
          const back = peopleViewReturnDockRef.current
          peopleViewReturnDockRef.current = null
          setDock(back && back !== 'people' ? back : 'scenarios')
          return
        }
        peopleViewReturnDockRef.current = dock
        setDock('people')
      }}
    >
      <span className="sc-presence" aria-hidden="true" />
      {`${viewingCount} viewing`}
    </button>
  )
}

/** Dock-only effects. Keep these off ScenariosPage so a tab click does not re-run it. */
export function StudioDockEffects({
  showComments,
  viewingTemplate,
  instanceId,
  libraryMode,
  hubNothingSelected,
  sessionOffersStoryClock,
  showTimelineAuthoring,
  templateInspectOffered,
  invitedIdsKey,
  selectedIsCompleted,
  chatRoomLinesLength,
  chatReaderId,
  timelineScrollId,
  onLeaveInspector,
  onLeaveTimeline,
  onEnterTimeline,
  onInspectOpenChange,
  onHubChooser,
  onSeenInvites,
  onChatOpened,
}: {
  showComments: boolean
  viewingTemplate: boolean
  instanceId: string | null
  libraryMode: boolean
  hubNothingSelected: boolean
  sessionOffersStoryClock: boolean
  showTimelineAuthoring: boolean
  templateInspectOffered: boolean
  invitedIdsKey: string
  selectedIsCompleted: boolean
  chatRoomLinesLength: number
  chatReaderId: string
  timelineScrollId: string
  onLeaveInspector: () => void
  onLeaveTimeline: () => void
  onEnterTimeline: () => void
  onInspectOpenChange: (open: boolean) => void
  onHubChooser: () => void
  onSeenInvites: (ids: string[]) => void
  onChatOpened: () => void
}) {
  const { dock, setDock } = useStudioDock()
  const inspectOpen = dock === 'inspector'

  useEffect(() => {
    onInspectOpenChange(inspectOpen)
  }, [inspectOpen, onInspectOpenChange])

  useEffect(() => {
    if (!showComments && dock === 'notes') setDock('inspector')
  }, [showComments, dock, setDock])

  useEffect(() => {
    if ((viewingTemplate || !instanceId) && dock === 'people') setDock('scenarios')
  }, [viewingTemplate, instanceId, dock, setDock])

  useEffect(() => {
    if (inspectOpen) return
    onLeaveInspector()
  }, [inspectOpen, onLeaveInspector])

  useEffect(() => {
    if (dock !== 'timeline') {
      onLeaveTimeline()
      return
    }
    onEnterTimeline()
  }, [dock, timelineScrollId, onLeaveTimeline, onEnterTimeline])

  useEffect(() => {
    if (!hubNothingSelected) return
    onHubChooser()
    if (dock === 'scenarios' || dock === 'account') return
    setDock('scenarios')
  }, [hubNothingSelected, dock, setDock, onHubChooser])

  useEffect(() => {
    if ((!sessionOffersStoryClock || !showTimelineAuthoring) && dock === 'timeline') {
      setDock('scenarios')
    }
  }, [sessionOffersStoryClock, showTimelineAuthoring, dock, setDock])

  useEffect(() => {
    if (templateInspectOffered) return
    if (dock === 'inspector') setDock('scenarios')
  }, [templateInspectOffered, dock, setDock])

  useEffect(() => {
    if (!(dock === 'scenarios' && !libraryMode)) return
    const ids = invitedIdsKey ? invitedIdsKey.split('|') : []
    if (!ids.length) return
    onSeenInvites(ids)
  }, [dock, libraryMode, invitedIdsKey, onSeenInvites])

  useEffect(() => {
    if (selectedIsCompleted) return
    if (dock !== 'notes' || !instanceId || chatRoomLinesLength === 0) return
    onChatOpened()
  }, [dock, instanceId, chatRoomLinesLength, chatReaderId, selectedIsCompleted, onChatOpened])

  return null
}
